// src/presentation/pages/ClinicalExamination/Step1SensoryAssessment.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { HearingSection, SpeechSection, VisionSection } from "./sections";

export interface SensoryFormData {
  // Vision
  visionStatus: string;
  visionAssistive: {
    wearsGlasses: boolean;
    usesContacts: boolean;
    sensitiveToLight: boolean;
  };
  visualAidPurpose: {
    forReading: boolean;
    forDistance: boolean;
    other: boolean;
  };
  visualAidOtherText: string;

  // Hearing
  hearingStatus: string;
  hearingAssistive: {
    usesHearingAid: boolean;
    hasCochlearImplant: boolean;
    usesSignLanguage: boolean;
  };

  // Speech
  speechStatus: string;
  communicationMethods: {
    speaksIndependently: boolean;
    usesAACDevice: boolean;
    usesGestures: boolean;
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export const initialSensoryFormData: SensoryFormData = {
  visionStatus: "",
  visionAssistive: {
    wearsGlasses: false,
    usesContacts: false,
    sensitiveToLight: false,
  },
  visualAidPurpose: {
    forReading: false,
    forDistance: false,
    other: false,
  },
  visualAidOtherText: "",
  hearingStatus: "",
  hearingAssistive: {
    usesHearingAid: false,
    hasCochlearImplant: false,
    usesSignLanguage: false,
  },
  speechStatus: "",
  communicationMethods: {
    speaksIndependently: false,
    usesAACDevice: false,
    usesGestures: false,
  },
};

interface Step1SensoryAssessmentProps {
  formData: SensoryFormData;
  onFormDataChange: (data: SensoryFormData) => void;
}

const Step1SensoryAssessment: React.FC<Step1SensoryAssessmentProps> = ({
  formData,
  onFormDataChange,
}) => {
  const { t } = useTranslation("patients");
  const updateFormData = (updates: Partial<SensoryFormData>) => {
    onFormDataChange({ ...formData, ...updates });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col">
        <p className="text-[#5F646F] text-sm"> {t("clinicalSections.step1.stepLabel")}</p>
        <div className="flex gap-2 items-center">
          <h3 className="text-lg font-bold text-gray-900 mb-1">
            {t("clinicalSections.step1.title")}
          </h3>
          <div className="bg-[#D4DAEB] p-2 rounded">
            <p className="">{t("clinicalSections.step1.answeredByPatient")}</p>
          </div>
        </div>
      </div>

      {/* Vision Section */}
      <VisionSection
        visionStatus={formData.visionStatus}
        visionAssistive={formData.visionAssistive}
        visualAidPurpose={formData.visualAidPurpose}
        visualAidOtherText={formData.visualAidOtherText}
        onVisionStatusChange={(value) =>
          updateFormData({ visionStatus: value })
        }
        onVisionAssistiveChange={(field, checked) =>
          updateFormData({
            visionAssistive: {
              ...formData.visionAssistive,
              [field]: checked,
            },
          })
        }
        onVisualAidPurposeChange={(field, checked) =>
          updateFormData({
            visualAidPurpose: {
              ...formData.visualAidPurpose,
              [field]: checked,
            },
          })
        }
        onVisualAidOtherTextChange={(value) =>
          updateFormData({ visualAidOtherText: value })
        }
      />

      {/* Hearing Section */}
      <HearingSection
        hearingStatus={formData.hearingStatus}
        hearingAssistive={formData.hearingAssistive}
        onHearingStatusChange={(value) =>
          updateFormData({ hearingStatus: value })
        }
        onHearingAssistiveChange={(field, checked) =>
          updateFormData({
            hearingAssistive: {
              ...formData.hearingAssistive,
              [field]: checked,
            },
          })
        }
      />

      {/* Speech Section */}
      <SpeechSection
        speechStatus={formData.speechStatus}
        communicationMethods={formData.communicationMethods}
        onSpeechStatusChange={(value) =>
          updateFormData({ speechStatus: value })
        }
        onCommunicationMethodsChange={(field, checked) =>
          updateFormData({
            communicationMethods: {
              ...formData.communicationMethods,
              [field]: checked,
            },
          })
        }
      />
    </div>
  );
};

export default Step1SensoryAssessment;
